/**
 * MediaEmbed — detects YouTube / Vimeo / Spotify / SoundCloud URLs
 * and renders the correct embed. Falls back to a plain link when the
 * URL isn't recognised.
 *
 * Usage:
 *   <MediaEmbed url="https://youtu.be/xyz"  title="…" />
 *   <MediaEmbed url="https://open.spotify.com/track/…" />
 */
const YT_HOSTS = ["youtube.com", "youtu.be", "www.youtube.com", "m.youtube.com"];
const VIMEO_HOSTS = ["vimeo.com", "www.vimeo.com", "player.vimeo.com"];
const SPOTIFY_HOSTS = ["open.spotify.com"];
const SOUNDCLOUD_HOSTS = ["soundcloud.com", "www.soundcloud.com"];

function _detect(url) {
  try {
    const u = new URL(url);
    if (YT_HOSTS.includes(u.host))       return { kind: "youtube",    u };
    if (VIMEO_HOSTS.includes(u.host))    return { kind: "vimeo",      u };
    if (SPOTIFY_HOSTS.includes(u.host))  return { kind: "spotify",    u };
    if (SOUNDCLOUD_HOSTS.includes(u.host)) return { kind: "soundcloud", u };
  } catch { /* not a URL */ }
  return { kind: null, u: null };
}

function _youtubeSrc(u) {
  // youtu.be/ID or youtube.com/watch?v=ID or /shorts/ID
  let id = null;
  if (u.host === "youtu.be") id = u.pathname.slice(1);
  else if (u.pathname.startsWith("/shorts/")) id = u.pathname.slice("/shorts/".length);
  else id = u.searchParams.get("v");
  if (!id) return null;
  id = id.split(/[/?&]/)[0];
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}`;
}

function _vimeoSrc(u) {
  const id = u.pathname.split("/").filter(Boolean)[0];
  if (!id) return null;
  return `https://player.vimeo.com/video/${encodeURIComponent(id)}`;
}

function _spotifySrc(u) {
  // Any spotify URL like /track/ID, /album/ID, /playlist/ID, /artist/ID
  const parts = u.pathname.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const [kind, id] = parts;
  const known = new Set(["track", "album", "playlist", "artist", "episode", "show"]);
  if (!known.has(kind)) return null;
  return `https://open.spotify.com/embed/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`;
}

function _soundcloudSrc(u) {
  return `https://w.soundcloud.com/player/?url=${encodeURIComponent(u.href)}&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`;
}

export default function MediaEmbed({ url, title, className = "" }) {
  if (!url) return null;
  const { kind, u } = _detect(url);

  let src = null;
  let aspect = "aspect-video";
  if (kind === "youtube")    src = _youtubeSrc(u);
  else if (kind === "vimeo") src = _vimeoSrc(u);
  else if (kind === "spotify") {
    src = _spotifySrc(u);
    aspect = ""; // Spotify iframe has its own height; use fixed 152/380
  } else if (kind === "soundcloud") {
    src = _soundcloudSrc(u);
    aspect = "";
  }

  if (!src) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
         className={`inline-flex items-center gap-1.5 underline ${className}`}
         style={{ color: "var(--wofy-primary)" }}
         data-testid="media-embed-fallback-link">
        {title || url}
      </a>
    );
  }

  // Fixed heights for Spotify/SoundCloud, aspect-video for video.
  const box = (kind === "spotify" || kind === "soundcloud")
    ? { height: kind === "spotify" ? 152 : 166 }
    : {};

  return (
    <div
      className={`overflow-hidden rounded-xl border ${aspect} ${className}`}
      style={{ borderColor: "var(--wofy-border)", ...box }}
      data-testid={`media-embed-${kind}`}
    >
      <iframe
        src={src}
        title={title || kind}
        allow={
          kind === "youtube" || kind === "vimeo"
            ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            : "encrypted-media"
        }
        allowFullScreen
        loading="lazy"
        className="w-full h-full border-0"
      />
    </div>
  );
}
